import { Button } from '@ads/components-react';
import { useTranslation } from 'react-i18next';

export interface UnsavedChangesModalProps {
  /** Confirma saída sem salvar — o chamador deve chamar `blocker.proceed()`. */
  readonly onLeave: () => void;
  /** Cancela a navegação e mantém o usuário no editor — `blocker.reset()`. */
  readonly onCancel: () => void;
}

/**
 * Modal de aviso de alterações não salvas.
 * Exibido quando o usuário tenta sair do editor com `isDirty = true`.
 * Fecha ao pressionar Escape ou clicar no overlay.
 */
export function UnsavedChangesModal({ onLeave, onCancel }: UnsavedChangesModalProps) {
  const { t } = useTranslation('slideEditor');

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay — click/keydown intentionally close the dialog
    <div
      className="fixed inset-none z-[999] flex items-center justify-center bg-base-neutral-black/deep"
      role="presentation"
      onClick={e => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div
        className="w-[90%] max-w-[24rem] rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level3"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="mb-xxs flex items-center justify-between">
          <span className="text-ctx-content-title ts-heading-sm">{t('unsavedModal.title')}</span>
          <button
            type="button"
            className="text-ctx-content-placeholder ts-caption-xxs hover:text-ctx-content-base"
            onClick={onCancel}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <p className="mb-xxs text-ctx-content-base ts-paragraph-xs">{t('unsavedModal.body')}</p>

        {/* Actions */}
        <div className="flex justify-end gap-nano">
          <Button hierarchy="secondary" size="md" onClick={onCancel}>
            {t('unsavedModal.cancel')}
          </Button>
          <Button hierarchy="danger" size="md" onClick={onLeave}>
            {t('unsavedModal.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
